import { MenuItem, Select, TextField, Typography, Box } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

interface DropdownProps {
  field: any;
  options: { id: string; label: string; [key: string]: any }[];
  label: string;
  error?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  renderOption?: (option: {
    id: string;
    label: string;
    [key: string]: any;
  }) => React.ReactNode;
}

const Dropdown = ({
  field,
  options,
  label,
  error,
  searchable = false,
  searchPlaceholder = "Buscar...",
  renderOption,
}: DropdownProps) => {
  const [searchValue, setSearchValue] = useState("");
  const menuPaperRef = useRef<HTMLDivElement | null>(null);

  const filteredOptions = searchable
    ? options.filter((option) => {
        const search = searchValue.toLowerCase();
        return Object.values(option).some(
          (value) =>
            typeof value === "string" && value.toLowerCase().includes(search),
        );
      })
    : options;

  useEffect(() => {
    if (menuPaperRef.current) {
      menuPaperRef.current.scrollTop = 0;
    }
  }, [searchValue]);

  return (
    <>
      <Select
        {...field}
        label={label}
        sx={{ width: "100%" }}
        onClose={() => setSearchValue("")}
        MenuProps={{
          PaperProps: {
            ref: menuPaperRef,
            style: {
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP + 56,
            },
          },
          MenuListProps: {
            sx: { pt: 0, pb: 0 },
          },
        }}
        renderValue={(selected) => {
          const selectedOption = options.find((o) => o.id === selected);
          return selectedOption?.label ?? "";
        }}
      >
        {searchable && (
          <MenuItem
            disableRipple
            disableTouchRipple
            onKeyDown={(e) => e.stopPropagation()}
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: "background.paper",
              zIndex: 10,
              py: 1,
              cursor: "default",
              "&:hover": {
                backgroundColor: "background.paper",
              },
            }}
          >
            <TextField
              size="small"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              fullWidth
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            />
          </MenuItem>
        )}
        {filteredOptions.map((role) => (
          <MenuItem key={role.id} value={role.id}>
            {renderOption ? renderOption(role) : role.label}
          </MenuItem>
        ))}
      </Select>

      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{
          opacity: error ? 1 : 0,
          height: error ? "auto" : 0,
        }}
        transition={{
          duration: 0.3,
          ease: "easeInOut",
        }}
        style={{ overflow: "hidden" }}
      >
        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{
              color: "red",
              display: "block",
              mt: 0.5,
            }}
          >
            {error}
          </Typography>
        )}
      </motion.div>
    </>
  );
};

export default Dropdown;
